import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Schedule,
  Search,
  Download,
  Add,
  Edit,
  Refresh,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { attendanceAPI, workersAPI } from "../services/api";
import { useSnackbar } from "notistack";

const AttendanceStatusChip = ({ status }) => {
  const getStatusProps = (status) => {
    switch (status) {
      case "present":
        return { color: "success", icon: <CheckCircle />, label: "Present" };
      case "absent":
        return { color: "error", icon: <Cancel />, label: "Absent" };
      case "half-day":
        return { color: "warning", icon: <Schedule />, label: "Half Day" };
      case "leave":
        return { color: "info", icon: <Schedule />, label: "On Leave" };
      default:
        return { color: "default", icon: <Schedule />, label: status };
    }
  };

  const props = getStatusProps(status);
  return (
    <Chip
      icon={props.icon}
      label={props.label}
      color={props.color}
      size="small"
    />
  );
};

const Attendance = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [attendanceData, setAttendanceData] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  // const [tabValue, setTabValue] = useState(0);
  const [openMarkDialog, setOpenMarkDialog] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [markingAttendance, setMarkingAttendance] = useState(false);

  const [attendanceForm, setAttendanceForm] = useState({
    workerId: "",
    status: "present",
    checkInTime: "",
    checkOutTime: "",
    remarks: "",
  });

  const [dailyStats, setDailyStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    halfDay: 0,
    leave: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAttendanceData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      
      // Use getAll with date parameter - this should return complete attendance records with status
      // The getByDate endpoint might not return status field
      const response = await attendanceAPI.getAll({ date: dateStr });
      
      // Handle different API response structures
      // getAll with date might return: {success: true, data: [{...attendance records...}]}
      // Or: {success: true, data: {records: [...], summary: {...}}}
      // NOTE: The records array has an unusual structure where attendance data is nested under "0" key
      // Example: [{workerId: "...", "0": {status: "present", checkIn: "...", ...}}]
      let attendanceArray = [];
      let rawRecords = [];
      
      if (response.data?.success && response.data?.data) {
        // If data is directly an array (most common for getAll)
        if (Array.isArray(response.data.data)) {
          rawRecords = response.data.data;
        }
        // If data has a records property
        else if (Array.isArray(response.data.data.records)) {
          rawRecords = response.data.data.records;
        }
        // If data has an attendance property
        else if (Array.isArray(response.data.data.attendance)) {
          rawRecords = response.data.data.attendance;
        }
        // If data is a single object, wrap it in array
        else if (response.data.data.workerId || response.data.data._id) {
          rawRecords = [response.data.data];
        }
      } 
      // Fallback to other possible structures
      else if (Array.isArray(response.data?.attendance)) {
        rawRecords = response.data.attendance;
      }
      else if (Array.isArray(response.data?.data)) {
        rawRecords = response.data.data;
      }
      else if (Array.isArray(response.data)) {
        rawRecords = response.data;
      }
      
      // Process raw records to handle nested structure
      // If records have attendance data nested under "0" key, extract it
      attendanceArray = rawRecords.map((record) => {
        // Check if attendance data is nested under "0" key
        if (record["0"] || record[0]) {
          const nestedData = record["0"] || record[0];
          
          // Normalize checkIn - handle both string and object formats
          let normalizedCheckIn = null;
          if (nestedData.checkIn) {
            if (typeof nestedData.checkIn === 'string') {
              normalizedCheckIn = { time: nestedData.checkIn, method: 'manual' };
            } else if (nestedData.checkIn.time) {
              normalizedCheckIn = nestedData.checkIn;
            }
          }
          
          // Normalize checkOut/checkout - handle both string and object formats, and both field names
          let normalizedCheckOut = null;
          const checkoutValue = nestedData.checkout || nestedData.checkOut;
          if (checkoutValue) {
            if (typeof checkoutValue === 'string') {
              normalizedCheckOut = { time: checkoutValue, method: 'manual' };
            } else if (checkoutValue.time) {
              normalizedCheckOut = checkoutValue;
            }
          }
          
          // Merge workerId from parent with nested attendance data
          return {
            ...nestedData,
            workerId: record.workerId || nestedData.workerId,
            // Override with normalized structures
            checkIn: normalizedCheckIn,
            checkOut: normalizedCheckOut,
            checkout: normalizedCheckOut, // Also preserve lowercase version for compatibility
            totalHours: nestedData.totalHours || nestedData.totalhours,
          };
        }
        // If record already has status at top level, use it as-is
        return record;
      });
      
      
      setAttendanceData(attendanceArray);
      
      const stats = {
        total: workers.length || 0,
        present: attendanceArray.filter((a) => 
          a.status === "present" || a.status === "Present"
        ).length,
        absent: attendanceArray.filter((a) => 
          a.status === "absent" || a.status === "Absent"
        ).length,
        halfDay: attendanceArray.filter((a) => 
          a.status === "half-day" || a.status === "halfDay" || a.status === "Half Day"
        ).length,
        leave: attendanceArray.filter((a) => 
          a.status === "leave" || a.status === "Leave" || a.status === "on-leave"
        ).length,
      };
      setDailyStats(stats);
    } catch (error) {
      console.error("Error loading attendance:", error);
      // Don't show error if it's just no data for the date
      if (error.response?.status !== 404) {
        enqueueSnackbar("Failed to load attendance data", { variant: "error" });
      }
      setAttendanceData([]);
      setDailyStats({
        total: workers.length || 0,
        present: 0,
        absent: 0,
        halfDay: 0,
        leave: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, enqueueSnackbar, workers.length]);

  const loadWorkers = useCallback(async () => {
    try {
      const response = await workersAPI.getAll({ status: "active" });
      setWorkers(response.data.workers || []);
    } catch (error) {
      console.error("Failed to load workers:", error);
    }
  }, []);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  useEffect(() => {
    if (workers.length > 0) {
      loadAttendanceData();
    }
  }, [selectedDate, workers.length, loadAttendanceData, refreshKey]);

  const handleMarkAttendance = async () => {
    setMarkingAttendance(true);
    try {
      const attendanceData = {
        ...attendanceForm,
        date: selectedDate.toISOString().split("T")[0],
        checkIn: attendanceForm.checkInTime
          ? {
              time: new Date(
                `${selectedDate.toISOString().split("T")[0]}T${
                  attendanceForm.checkInTime
                }`
              ),
              method: "manual",
            }
          : null,
        checkOut: attendanceForm.checkOutTime
          ? {
              time: new Date(
                `${selectedDate.toISOString().split("T")[0]}T${
                  attendanceForm.checkOutTime
                }`
              ),
              method: "manual",
            }
          : null,
      };

      const response = await attendanceAPI.markAttendance(attendanceData);
      
      enqueueSnackbar("Attendance marked successfully", { variant: "success" });

      // Immediately update local state with the returned attendance record
      if (response.data?.success && response.data?.data) {
        let newAttendanceRecord = response.data.data;
        
        // Normalize the response structure (handle nested "0" key if present)
        if (newAttendanceRecord["0"] || newAttendanceRecord[0]) {
          const nestedData = newAttendanceRecord["0"] || newAttendanceRecord[0];
          newAttendanceRecord = {
            ...nestedData,
            workerId: newAttendanceRecord.workerId || nestedData.workerId,
            checkIn: nestedData.checkIn ? 
              (typeof nestedData.checkIn === 'string' ? { time: nestedData.checkIn } : nestedData.checkIn) :
              null,
            checkOut: nestedData.checkout || nestedData.checkOut ?
              (typeof (nestedData.checkout || nestedData.checkOut) === 'string' ? 
                { time: nestedData.checkout || nestedData.checkOut } : 
                (nestedData.checkOut || nestedData.checkout)) :
              null,
            totalHours: nestedData.totalHours || nestedData.totalhours,
          };
        }
        
        setAttendanceData(prevData => {
          // Remove existing record for this worker if present
          const filtered = prevData.filter(
            a => {
              const aWorkerId = String(a.workerId || a.worker?._id || a.worker?.id || '');
              const newWorkerId = String(newAttendanceRecord.workerId || '');
              return aWorkerId !== newWorkerId;
            }
          );
          // Add the new/updated record
          const updated = [...filtered, newAttendanceRecord];
          
          // Update stats immediately with the updated data
          setDailyStats({
            total: workers.length || 0,
            present: updated.filter((a) => 
              a.status === "present" || a.status === "Present"
            ).length,
            absent: updated.filter((a) => 
              a.status === "absent" || a.status === "Absent"
            ).length,
            halfDay: updated.filter((a) => 
              a.status === "half-day" || a.status === "halfDay" || a.status === "Half Day"
            ).length,
            leave: updated.filter((a) => 
              a.status === "leave" || a.status === "Leave" || a.status === "on-leave"
            ).length,
          });
          
          return updated;
        });
      }

      // Reset form
      setAttendanceForm({
        workerId: "",
        status: "present",
        checkInTime: "",
        checkOutTime: "",
        remarks: "",
      });
      setOpenMarkDialog(false);
      setSelectedWorker(null);
      
      // Also trigger a full reload after a short delay to ensure consistency
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
        loadAttendanceData();
      }, 300);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to mark attendance",
        { variant: "error" }
      );
    } finally {
      setMarkingAttendance(false);
    }
  };

  const handleBulkMarkPresent = async () => {
    if (window.confirm("Mark all workers as present for today?")) {
      try {
        const bulkData = workers.map((worker) => ({
          workerId: worker._id,
          date: selectedDate.toISOString().split("T")[0],
          status: "present",
          checkIn: {
            time: new Date(
              `${selectedDate.toISOString().split("T")[0]}T09:00:00`
            ),
            method: "manual",
          },
        }));

        await attendanceAPI.bulkMarkAttendance(bulkData);
        
        // Wait a bit to ensure backend has processed
        await new Promise(resolve => setTimeout(resolve, 300));
        
        enqueueSnackbar("Bulk attendance marked successfully", {
          variant: "success",
        });
        
        // Force reload attendance data by incrementing refresh key
        setRefreshKey(prev => prev + 1);
        
        // Also directly call loadAttendanceData
        await loadAttendanceData();
      } catch (error) {
        enqueueSnackbar("Failed to mark bulk attendance", { variant: "error" });
      }
    }
  };

  // Create a map of attendance records by workerId for quick lookup
  // Handle different ID field names
  const attendanceMap = new Map();
  attendanceData.forEach((record) => {
    // Try multiple possible field names for workerId
    const workerId = record.workerId || 
                     record.worker_id || 
                     record.worker?._id || 
                     record.worker?.id ||
                     record.workerId;
    
    if (workerId) {
      // Store with multiple keys to handle different ID formats
      // Convert to string to ensure consistent comparison
      const workerIdStr = String(workerId);
      attendanceMap.set(workerIdStr, record);
      // Also store with the original worker object ID if different
      if (record.worker?._id && String(record.worker._id) !== workerIdStr) {
        attendanceMap.set(String(record.worker._id), record);
      }
      if (record._id) {
        attendanceMap.set(String(record._id), record);
      }
    }
  });
  

  // Show all workers, with their attendance if marked
  const filteredWorkers = workers.filter((worker) => {
    const matchesSearch =
      `${worker.personalDetails?.firstName || ''} ${worker.personalDetails?.lastName || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (worker.workerId || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === "all") return true;

    const workerId = worker._id || worker.id;
    const attendance = attendanceMap.get(workerId) || 
                      attendanceData.find(a => {
                        const aWorkerId = a.workerId || a.worker?._id || a.worker?.id;
                        return aWorkerId === workerId;
                      });
    if (!attendance) {
      // If no attendance marked, show only if filtering for "all" or "absent"
      return filterStatus === "absent" || filterStatus === "all";
    }

    return attendance.status === filterStatus;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Attendance Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage worker attendance
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBulkMarkPresent}
              startIcon={<CheckCircle />}
            >
              Mark All Present
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenMarkDialog(true)}
            >
              Mark Attendance
            </Button>
          </Box>
        </Box>

        {/* Date Selector & Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={new Date()}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h4" color="primary.main">
                      {dailyStats.present}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Present
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h4" color="error.main">
                      {dailyStats.absent}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Absent
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h4" color="warning.main">
                      {dailyStats.halfDay}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Half Day
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h4" color="info.main">
                      {dailyStats.leave}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      On Leave
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent sx={{ textAlign: "center", py: 2 }}>
                    <Typography variant="h4" color="text.primary">
                      {dailyStats.total > 0
                        ? (
                            (dailyStats.present / dailyStats.total) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attendance Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search workers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                    <MenuItem value="half-day">Half Day</MenuItem>
                    <MenuItem value="leave">On Leave</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={async () => {
                    setRefreshKey(prev => prev + 1);
                    await loadAttendanceData();
                  }}
                  fullWidth
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" startIcon={<Download />} fullWidth>
                  Export
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Attendance Table */}
        <TableContainer component={Paper}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Worker</TableCell>
                <TableCell>Check In</TableCell>
                <TableCell>Check Out</TableCell>
                <TableCell>Total Hours</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No workers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredWorkers.map((worker) => {
                  // Try multiple ID fields to match attendance
                  const workerId = String(worker._id || worker.id);
                  
                  // Try direct map lookup first
                  let attendance = attendanceMap.get(workerId);
                  
                  // If not found, try finding in array with various ID formats
                  if (!attendance) {
                    attendance = attendanceData.find(a => {
                      const aWorkerId = String(
                        a.workerId || 
                        a.worker_id || 
                        a.worker?._id || 
                        a.worker?.id || 
                        ''
                      );
                      return aWorkerId === workerId;
                    });
                  }
                  
                  const hasAttendance = !!attendance;
                  

                  return (
                    <TableRow key={worker._id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2 }}>
                            {worker.personalDetails?.firstName?.[0]?.toUpperCase() || 'W'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {worker.personalDetails?.firstName || ''}{" "}
                              {worker.personalDetails?.lastName || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {worker.workerId || worker._id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Handle multiple formats: object with time property, or string directly
                          const checkInTime = attendance?.checkIn?.time || 
                                             (typeof attendance?.checkIn === 'string' ? attendance.checkIn : null) ||
                                             null;
                          
                          if (checkInTime) {
                            try {
                              return (
                                <Box>
                                  <Typography variant="body2">
                                    {new Date(checkInTime).toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {attendance.checkIn?.method || 'manual'}
                                  </Typography>
                                </Box>
                              );
                            } catch (e) {
                              return "-";
                            }
                          }
                          return "-";
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Handle multiple formats: object with time property, or string directly
                          const checkOutTime = attendance?.checkOut?.time || 
                                              (typeof attendance?.checkOut === 'string' ? attendance.checkOut : null) ||
                                              attendance?.checkout?.time ||
                                              (typeof attendance?.checkout === 'string' ? attendance.checkout : null) ||
                                              null;
                          
                          if (checkOutTime) {
                            try {
                              return (
                                <Box>
                                  <Typography variant="body2">
                                    {new Date(checkOutTime).toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {attendance.checkOut?.method || attendance?.checkout?.method || 'manual'}
                                  </Typography>
                                </Box>
                              );
                            } catch (e) {
                              return "-";
                            }
                          }
                          return "-";
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {attendance?.totalHours
                            ? `${attendance.totalHours.toFixed(1)}h`
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {hasAttendance ? (
                          <AttendanceStatusChip status={attendance.status} />
                        ) : (
                          <Chip label="Not Marked" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {attendance?.remarks || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedWorker(worker);
                            setAttendanceForm({
                              workerId: worker._id,
                              status: attendance?.status || "present",
                              checkInTime: attendance?.checkIn?.time
                                ? new Date(attendance.checkIn.time).toTimeString().slice(0, 5)
                                : "",
                              checkOutTime: attendance?.checkOut?.time
                                ? new Date(attendance.checkOut.time).toTimeString().slice(0, 5)
                                : "",
                              remarks: attendance?.remarks || "",
                            });
                            setOpenMarkDialog(true);
                          }}
                          title="Mark Attendance"
                        >
                          <CheckCircle />
                        </IconButton>
                        {hasAttendance && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedWorker(worker);
                              setAttendanceForm({
                                workerId: worker._id,
                                status: attendance.status,
                                checkInTime: attendance.checkIn?.time
                                  ? new Date(attendance.checkIn.time).toTimeString().slice(0, 5)
                                  : "",
                                checkOutTime: attendance.checkOut?.time
                                  ? new Date(attendance.checkOut.time).toTimeString().slice(0, 5)
                                  : "",
                                remarks: attendance.remarks || "",
                              });
                              setOpenMarkDialog(true);
                            }}
                            title="Edit Attendance"
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mark Attendance Dialog */}
        <Dialog
          open={openMarkDialog}
          onClose={() => {
            setOpenMarkDialog(false);
            setSelectedWorker(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedWorker
              ? `Mark Attendance - ${selectedWorker.personalDetails?.firstName || ''} ${selectedWorker.personalDetails?.lastName || ''}`
              : "Mark Attendance"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Worker</InputLabel>
                  <Select
                    value={attendanceForm.workerId}
                    onChange={(e) =>
                      setAttendanceForm({
                        ...attendanceForm,
                        workerId: e.target.value,
                      })
                    }
                  >
                    {workers.map((worker) => (
                      <MenuItem key={worker._id} value={worker._id}>
                        {worker.personalDetails.firstName}{" "}
                        {worker.personalDetails.lastName} - {worker.workerId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={attendanceForm.status}
                    onChange={(e) =>
                      setAttendanceForm({
                        ...attendanceForm,
                        status: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                    <MenuItem value="half-day">Half Day</MenuItem>
                    <MenuItem value="leave">On Leave</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Check In Time"
                  type="time"
                  value={attendanceForm.checkInTime}
                  onChange={(e) =>
                    setAttendanceForm({
                      ...attendanceForm,
                      checkInTime: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Check Out Time"
                  type="time"
                  value={attendanceForm.checkOutTime}
                  onChange={(e) =>
                    setAttendanceForm({
                      ...attendanceForm,
                      checkOutTime: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  multiline
                  rows={3}
                  value={attendanceForm.remarks}
                  onChange={(e) =>
                    setAttendanceForm({
                      ...attendanceForm,
                      remarks: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMarkDialog(false)}>Cancel</Button>
            <Button
              onClick={handleMarkAttendance}
              variant="contained"
              disabled={markingAttendance || !attendanceForm.workerId}
            >
              {markingAttendance ? "Marking..." : "Mark Attendance"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Attendance;
